import { useMutation, useQueryClient } from '@tanstack/react-query';
import fetchClient from '@eureca/recruiter-api-sdk/client';
import type {
  PatchJobsIdStatus200,
  PatchJobsIdStatusMutationRequest,
} from '@eureca/recruiter-api-sdk/types';
import { JobMutationError } from '@/features/jobs/job-shared/job-edit-mutation';

/**
 * Mutation for `PATCH /jobs/:id/status` — single-row encerrar/cancelar.
 * Refreshes the cached list so the row's badge updates without a refetch.
 *
 * (Path renamed from `/journeys/:id/status` after the core refactor; SPA
 * keeps the function name and surrounding UX unchanged.)
 *
 * Errors:
 *   - `status === 422` carries `code: 'invalid_transition'` in the body —
 *     callers should show an actionable inline message rather than a
 *     generic error toast.
 *   - `status === 404` → job deleted by another tab; refetch the list.
 *   - `status === 401|403` → consultor lost scope; bounce to login.
 */
export function useTransitionJobStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      status: PatchJobsIdStatusMutationRequest['status'];
    }): Promise<PatchJobsIdStatus200> => {
      const res = await fetchClient<PatchJobsIdStatus200>({
        method: 'PATCH',
        url: `/jobs/${input.id}/status`,
        data: { status: input.status },
      });
      if (res.error) {
        throw new JobMutationError(
          `Failed to transition job status (status ${res.error.status})`,
          res.error.status
        );
      }
      return res.data;
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['recruiter', 'jobs'] });
      void queryClient.invalidateQueries({
        queryKey: ['recruiter', 'jobs', 'detail', variables.id],
      });
    },
  });
}

/**
 * Mutation for `POST /jobs/:id/duplicate` — deep-copies a job (row +
 * `job_position` + `job_step` + `module` + `form_module`). The new row is
 * `rascunho` (statusId=1, publishedAt=null) with name suffixed " (Cópia)".
 * Subscriptions / step-progress / module-responses are NOT cloned.
 *
 * Returns the cloned `id` (the SPA navigates straight into the edit hub).
 */
export function useDuplicateJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string }): Promise<{ id: string }> => {
      const res = await fetchClient<{ id: string }>({
        method: 'POST',
        url: `/jobs/${input.id}/duplicate`,
      });
      if (res.error) {
        throw new JobMutationError(
          `Failed to duplicate job (status ${res.error.status})`,
          res.error.status
        );
      }
      return res.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['recruiter', 'jobs'] });
    },
  });
}
